/**
 * Parses raw tokens into a typed AST.
 *
 * The AST is the wire format. The UI evaluates it directly against loaded rows
 * for instant feedback; the Python sidecar compiles the same AST to SQL for the
 * full library. That keeps one parser rather than two implementations that drift
 * apart -- the classic failure of hand-rolled query languages.
 */

import { toCamelot } from "../format/keys";
import { tokenize, type RawToken } from "./tokenize";

export type FieldType = "text" | "number" | "key" | "enum" | "bool";

export interface FieldSpec {
  name: string;
  type: FieldType;
  aliases?: string[];
  /** Human description, surfaced in autocomplete. */
  hint: string;
  /** Valid values for enum fields. */
  values?: string[];
}

/** The searchable surface. Adding a field here makes it work everywhere. */
export const FIELDS: FieldSpec[] = [
  { name: "title", type: "text", hint: "Track title" },
  { name: "artist", type: "text", hint: "Artist name" },
  { name: "album", type: "text", hint: "Album name" },
  { name: "year", type: "number", hint: "Release year, e.g. 2020..2024" },
  { name: "genre", type: "text", hint: "Genre, supports subtree with /*" },
  { name: "subgenre", type: "text", aliases: ["sub"], hint: "Precise sub-genre" },
  { name: "comment", type: "text", aliases: ["note", "notes"], hint: "Comment tag" },
  { name: "path", type: "text", aliases: ["folder"], hint: "File path within the library" },
  { name: "bpm", type: "number", aliases: ["tempo"], hint: "Tempo, e.g. 100-115" },
  { name: "key", type: "key", hint: "Camelot (8A) or name (A minor)" },
  { name: "energy", type: "number", hint: "0.0 - 1.0" },
  { name: "lufs", type: "number", aliases: ["loudness"], hint: "Integrated loudness, LUFS" },
  { name: "lra", type: "number", hint: "Loudness range, LU" },
  { name: "truepeak", type: "number", aliases: ["peak"], hint: "Peak, dBFS (the loudest sample; a true peak on songs measured before October 2026)" },
  { name: "cuein", type: "number", hint: "Audio start, seconds" },
  { name: "cueout", type: "number", hint: "Audio end, seconds" },
  { name: "duration", type: "number", aliases: ["length"], hint: "Seconds" },
  { name: "intro", type: "number", hint: "Talk-over point, seconds" },
  { name: "danceability", type: "number", aliases: ["dance"], hint: "0.0 - 1.0" },
  { name: "energylevel", type: "number", aliases: ["level"], hint: "Energy level 1 - 5" },
  {
    name: "tonality",
    type: "enum",
    hint: "Harmonic framework",
    values: ["western", "modal", "pentatonic", "mixed"],
  },
  {
    name: "confidence",
    type: "enum",
    aliases: ["conf"],
    hint: "Analysis confidence band",
    values: ["high", "medium", "low"],
  },
  { name: "reviewed", type: "bool", hint: "Whether a curator has confirmed it" },
  { name: "analysed", type: "bool", aliases: ["analyzed"], hint: "BPM and key predicted" },
  { name: "measured", type: "bool", hint: "Deterministic measurements taken" },
  // Archived and missing tracks are hidden from the grid, so the query language
  // is the only way back to them. That is the whole point of hiding rather than
  // deleting: the data is still there and still findable.
  { name: "archived", type: "bool", hint: "Retired by a curator, hidden from the grid" },
  { name: "missing", type: "bool", hint: "File no longer on disk" },
  { name: "needsedit", type: "bool", hint: "Flagged as needing an edit (dirty lyrics, wrong tags…)" },
];

const FIELD_INDEX = new Map<string, FieldSpec>();
for (const spec of FIELDS) {
  FIELD_INDEX.set(spec.name, spec);
  for (const a of spec.aliases ?? []) FIELD_INDEX.set(a, spec);
}

/** `anyOf` (any of several values) is produced by the filter panel, never by typing. */
export type Comparator = "eq" | "lt" | "lte" | "gt" | "gte" | "range" | "contains" | "prefix" | "anyOf";

export interface Clause {
  field: string;
  type: FieldType;
  cmp: Comparator;
  negated: boolean;
  value: string | number | boolean | string[];
  /** Upper bound, for `range`. */
  value2?: number;
  /** True for `genre:amapiano/*` subtree queries. */
  subtree?: boolean;
  token: RawToken;
}

export interface FreeText {
  text: string;
  negated: boolean;
  token: RawToken;
}

export interface ParseError {
  message: string;
  start: number;
  end: number;
  text: string;
}

export interface Query {
  clauses: Clause[];
  free: FreeText[];
  errors: ParseError[];
  /** True when the query has no usable terms. */
  isEmpty: boolean;
}

const OP_MAP: Record<string, Comparator> = {
  ">": "gt",
  ">=": "gte",
  "<": "lt",
  "<=": "lte",
  "=": "eq",
};

const RANGE_RE = /^(-?\d+(?:\.\d+)?)(?:\.\.|-)(-?\d+(?:\.\d+)?)$/;
const CAMELOT_RE = /^(1[0-2]|[1-9])[AB]$/i;

export function parse(input: string): Query {
  const clauses: Clause[] = [];
  const free: FreeText[] = [];
  const errors: ParseError[] = [];

  for (const token of tokenize(input)) {
    if (token.kind === "error") {
      errors.push({
        message: token.message ?? "invalid term",
        start: token.start,
        end: token.end,
        text: token.text,
      });
      continue;
    }

    if (token.kind === "bare") {
      if (token.value) free.push({ text: token.value, negated: token.negated, token });
      continue;
    }

    const spec = FIELD_INDEX.get(token.field!);
    if (!spec) {
      errors.push({
        message: `unknown field '${token.field}'`,
        start: token.start,
        end: token.end,
        text: token.text,
      });
      continue;
    }

    const clause = buildClause(spec, token);
    if ("message" in clause) {
      errors.push({
        message: clause.message,
        start: token.start,
        end: token.end,
        text: token.text,
      });
    } else {
      clauses.push(clause);
    }
  }

  return {
    clauses,
    free,
    errors,
    isEmpty: clauses.length === 0 && free.length === 0,
  };
}

function buildClause(
  spec: FieldSpec,
  token: RawToken
): Clause | { message: string } {
  const raw = (token.value ?? "").trim();
  const base = { field: spec.name, type: spec.type, negated: token.negated, token };

  switch (spec.type) {
    case "number": {
      if (!token.quoted) {
        const range = RANGE_RE.exec(raw);
        if (range) {
          const lo = Number(range[1]);
          const hi = Number(range[2]);
          if (lo > hi) return { message: `range ${raw} is inverted` };
          return { ...base, cmp: "range", value: lo, value2: hi };
        }
      }
      const n = Number(raw);
      if (!Number.isFinite(n)) {
        return { message: `'${raw}' is not a number for ${spec.name}` };
      }
      return { ...base, cmp: OP_MAP[token.op ?? "="] ?? "eq", value: n };
    }

    case "key": {
      if (raw.toLowerCase() === "ambiguous") {
        return { ...base, cmp: "eq", value: "ambiguous" };
      }
      // Camelot first, then the traditional name. Nova stores the code, so a
      // name is translated to one rather than matched separately -- otherwise
      // key:"A minor" and key:8A would be two filters over the same tracks.
      if (CAMELOT_RE.test(raw)) {
        return { ...base, cmp: "eq", value: raw.toUpperCase() };
      }
      const asCamelot = toCamelot(raw);
      if (asCamelot) {
        return { ...base, cmp: "eq", value: asCamelot };
      }
      return {
        message: `'${raw}' is not a key (try 8A, 11B, "A minor" or "Eb major")`,
      };
    }

    case "enum": {
      const v = raw.toLowerCase();
      if (spec.values && !spec.values.includes(v)) {
        return {
          message: `${spec.name} must be one of ${spec.values.join(", ")}`,
        };
      }
      return { ...base, cmp: "eq", value: v };
    }

    case "bool": {
      const v = raw.toLowerCase();
      const truthy = ["yes", "true", "1"].includes(v);
      const falsy = ["no", "false", "0"].includes(v);
      if (!truthy && !falsy) {
        return { message: `${spec.name} expects yes or no` };
      }
      return { ...base, cmp: "eq", value: truthy };
    }

    case "text":
    default: {
      // genre:amapiano/*  -> match the node and everything under it
      if (raw.endsWith("/*")) {
        return { ...base, cmp: "prefix", value: raw.slice(0, -2), subtree: true };
      }
      return { ...base, cmp: "contains", value: raw };
    }
  }
}

/** Field suggestions for the given partial input, for autocomplete. */
export function suggestFields(partial: string): FieldSpec[] {
  const p = partial.toLowerCase();
  if (!p) return FIELDS;
  return FIELDS.filter(
    (f) => f.name.startsWith(p) || (f.aliases ?? []).some((a) => a.startsWith(p))
  );
}
